import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { BaseAction } from "../../../core/actions/BaseAction";
import { ExecutionStatus } from "../../../core/enum/ExecutionStatus";
import { uuid } from "../../../core/utils/Uuid";
import { getSearchSelectedPassenger } from "../../../Lib/Utils/passenger";
import { determinePassengerType, getTrainClassTypeBaseOnProduct, isActionRunSuccessfully } from "../../../Lib/Utils/validationUtil";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { ProductCodeEnum, TrainTypeEnum, TripTypeEnum, SHUTTLE_PRODUCT_CODE } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBookedOffer, IBookedTrips, IBooking, IBookingTypeTrip, IDeterminePassenger, IOffer, IOffersResponse, IPassenger, IReservation, ISelectedDisabledCalenderDate, ITrip, ITripOfferModel, SearchStateType } from "../../../Orchestrator/Types/Index";
import { ISelectDateClickActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/BookItAgainActionParams";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";
import { IBookingRequest } from "../../../Orchestrator/Types/Types/BookingTypes";
import { TrainType, ITrainTypeClass } from "../../../Orchestrator/Types/Types/TrainTypes";
import { getAllOfferWithoutExchangeAction } from "../../LogicActions/CommonActions/GetAllOfferWithoutExchangeAction";
import { getAllTripWithoutExchangeAction } from "../../LogicActions/CommonActions/GetAllTripWithoutExchangeAction";
import { getSearchResultAction } from "../../LogicActions/Search/GetSearchResultAction";
import { convertOffersToTrainsAction } from "./ConvertOffersToTrainsAction";
import { processBookItAgainOfferAction } from "./ProcessBookItAgainOfferAction";
dayjs.extend(timezone);
dayjs.extend(utc);

export class BookItAgainDateClickAction extends BaseAction<IDefaultActionResult, ISelectDateClickActionParams> {

    convertPassengersToCount(passengers: IPassenger[]): Record<string, number> {
        const counts: Record<string, number> = {};
        passengers.forEach(passenger => {
            const determinePassenger: IDeterminePassenger = {
                age: passenger?.age,
                cards: passenger?.cards || [],
                prmNeeds: ((passenger?.prmNeeds || []).length > 0 && passenger?.prmNeeds) ? passenger?.prmNeeds[0] : ""
            }
            const type = determinePassengerType(determinePassenger);
            counts[type] = (counts[type] || 0) + 1;
        });
        return counts;
    }

    async checkOfferAvailableOrNot(train: TrainType, selectedTrip: IBookedTrips, actionResult: IDefaultActionResult, selectedBookingForBookItAgain: IBooking | undefined, direction: string) {

        const passengerRefs: string[] = [];
        selectedTrip.passengers.forEach(element => {
            passengerRefs.push(element.externalRef);
        });

        const offers: any = [];
        const offerIds: string[] = [];
        const selectedOffers: Partial<IOffer>[] = [];
        let priceClass: string = "not_found"
        let price: number | null = null
        let hasTrainLegs: boolean = false
        let hasShuttleLegs: boolean = false

        let bookedTrip: ITrip | undefined = undefined
        const bookedOffersForMatching: IBookedOffer[] = []
        if (selectedBookingForBookItAgain) {
            bookedTrip = selectedBookingForBookItAgain.trips.find(d => d.direction === direction)
            if (bookedTrip) {
                bookedOffersForMatching.push(...selectedBookingForBookItAgain.bookedOffers.filter((d) => d?.tripCoverage?.coveredTripId === bookedTrip?.id))
            }
        }

        const bookedProductCodes = new Set<string>()
        bookedOffersForMatching.forEach(bo => {
            bo.products?.forEach(product => {
                if (product.code) {
                    bookedProductCodes.add(product.code)
                }
            })
        })

        train?.legs?.forEach((leg) => {
            if (leg.timedLeg.service.mode.ptMode === "TRAIN") {
                hasTrainLegs = true
                if (train.selectedPriceClass === "premium") {
                    const premiumOffers = leg.legOffer?.filter(offer => offer.products[0].code === ProductCodeEnum.PFF) || []
                    if (premiumOffers.length > 0) {
                        priceClass = "premium"
                        premiumOffers.forEach(offer => {
                            if (offer?.offerId && !selectedOffers.find(o => o.offerId === offer.offerId)) {
                                offerIds.push(offer.offerId)
                                selectedOffers.push(offer)
                            }
                        })
                    }
                } else if (train.selectedPriceClass === "standardValue") {
                    const standardValueOffers = leg.legOffer?.filter(offer => offer.products[0].code === ProductCodeEnum.SFC) || []
                    if (standardValueOffers.length > 0) {
                        priceClass = "standardValue"
                        standardValueOffers.forEach(offer => {
                            if (offer?.offerId && !selectedOffers.find(o => o.offerId === offer.offerId)) {
                                offerIds.push(offer.offerId)
                                selectedOffers.push(offer)
                            }
                        })
                    }
                } else if (train.selectedPriceClass === "standardPro") {
                    const standardProOffers = leg.legOffer?.filter(offer => offer.products[0].code === ProductCodeEnum.SFF) || []
                    if (standardProOffers.length > 0) {
                        priceClass = "standardPro"
                        standardProOffers.forEach(offer => {
                            if (offer?.offerId && !selectedOffers.find(o => o.offerId === offer.offerId)) {
                                offerIds.push(offer.offerId)
                                selectedOffers.push(offer)
                            }
                        })
                    }
                } else if (train.selectedPriceClass === "standardDuo") {
                    const standardDuoOffers = leg.legOffer?.filter(offer => offer.products[0].code === ProductCodeEnum.STDNFDUO) || []
                    if (standardDuoOffers.length > 0) {
                        priceClass = "standardDuo"
                        standardDuoOffers.forEach(offer => {
                            if (offer?.offerId && !selectedOffers.find(o => o.offerId === offer.offerId)) {
                                offerIds.push(offer.offerId)
                                selectedOffers.push(offer)
                            }
                        })
                    }
                } else if (train.selectedPriceClass === "standardQuad") {
                    const standardQuadOffers = leg.legOffer?.filter(offer => offer.products[0].code === ProductCodeEnum.STDNFQUAD) || []
                    if (standardQuadOffers.length > 0) {
                        priceClass = "standardQuad"
                        standardQuadOffers.forEach(offer => {
                            if (offer?.offerId && !selectedOffers.find(o => o.offerId === offer.offerId)) {
                                offerIds.push(offer.offerId)
                                selectedOffers.push(offer)
                            }
                        })
                    }
                } else if (train.selectedPriceClass === "premiumDuo") {
                    const premiumDuoOffers = leg.legOffer?.filter(offer => offer.products[0].code === ProductCodeEnum.PREMNFDUO) || []
                    if (premiumDuoOffers.length > 0) {
                        priceClass = "premiumDuo"
                        premiumDuoOffers.forEach(offer => {
                            if (offer?.offerId && !selectedOffers.find(o => o.offerId === offer.offerId)) {
                                offerIds.push(offer.offerId)
                                selectedOffers.push(offer)
                            }
                        })
                    }
                } else if (train.selectedPriceClass === "premiumQuad") {
                    const premiumQuadOffers = leg.legOffer?.filter(offer => offer.products[0].code === ProductCodeEnum.PREMNFQUAD) || []
                    if (premiumQuadOffers.length > 0) {
                        priceClass = "premiumQuad"
                        premiumQuadOffers.forEach(offer => {
                            if (offer?.offerId && !selectedOffers.find(o => o.offerId === offer.offerId)) {
                                offerIds.push(offer.offerId)
                                selectedOffers.push(offer)
                            }
                        })
                    }
                } else {
                    const matchingOffers = leg.legOffer?.filter(offer => {
                        const offerProductCode = offer.products?.[0]?.code
                        return offerProductCode && bookedProductCodes.has(offerProductCode)
                    }) || []
                    
                    matchingOffers.forEach(offer => {
                        if (offer?.offerId && !selectedOffers.find(o => o.offerId === offer.offerId)) {
                            offerIds.push(offer.offerId)
                            selectedOffers.push(offer)
                        }
                    })
                }
            } else {
                //This is for shuttle
                hasShuttleLegs = true
                if (leg?.legOffer) {
                    const shuttleOffers = leg.legOffer.filter(offer => {
                        const offerProductCode = offer.products?.[0]?.code
                        return offerProductCode === SHUTTLE_PRODUCT_CODE && (!bookedProductCodes.size || bookedProductCodes.has(offerProductCode))
                    })
                    
                    shuttleOffers.forEach(offer => {
                        if (offer?.offerId && !selectedOffers.find(o => o.offerId === offer.offerId)) {
                            offerIds.push(offer.offerId)
                            selectedOffers.push(offer)
                        }
                    })
                }
            }
        })

        if (hasTrainLegs && !train.selectedPriceClass) {
            const hasPremiumOffers = selectedOffers.some(offer => offer.products?.[0]?.code === ProductCodeEnum.PFF)
            const hasStandardProOffers = selectedOffers.some(offer => offer.products?.[0]?.code === ProductCodeEnum.SFF)
            const hasStandardValueOffers = selectedOffers.some(offer => offer.products?.[0]?.code === ProductCodeEnum.SFC)
            const hasStandardDuoOffers = selectedOffers.some(offer => offer.products?.[0]?.code === ProductCodeEnum.STDNFDUO)
            const hasStandardQuadOffers = selectedOffers.some(offer => offer.products?.[0]?.code === ProductCodeEnum.STDNFQUAD)
            const hasPremiumDuoOffers = selectedOffers.some(offer => offer.products?.[0]?.code === ProductCodeEnum.PREMNFDUO)
            const hasPremiumQuadOffers = selectedOffers.some(offer => offer.products?.[0]?.code === ProductCodeEnum.PREMNFQUAD)
            
            if (hasPremiumOffers) {
                priceClass = "premium"
            } else if (hasStandardProOffers) {
                priceClass = "standardPro"
            } else if (hasStandardValueOffers) {
                priceClass = "standardValue"
            } else if (hasPremiumQuadOffers) {
                priceClass = "premiumQuad"
            } else if (hasPremiumDuoOffers) {
                priceClass = "premiumDuo"
            } else if (hasStandardQuadOffers) {
                priceClass = "standardQuad"
            } else if (hasStandardDuoOffers) {
                priceClass = "standardDuo"
            }
        }

        if (!hasTrainLegs && hasShuttleLegs && priceClass === "not_found") {
            priceClass = ""
        }

        if (priceClass === "not_found") {
            actionResult.status = "fail"
            actionResult.message = "Already booked booking class type offer not available"
            return "fail"
        }
        
        // Calculate total price from all collected offers
        let totalPrice = 0
        selectedOffers.forEach(offer => {
            const parts = offer?.admissionOfferParts || []
            if (parts.length > 0) {
                const firstScale = parts[0]?.price?.scale || 2
                const offerPrice = parts.reduce((sum, p) => sum + (p?.price?.amount || 0), 0) / ((100 * firstScale) / firstScale)
                totalPrice += offerPrice
            }
        })
        price = totalPrice

        if (train) { //Outbound train
            updateRecoilState("selectedOutboundTrainAtom", train);
        }

        if (offerIds.length > 0 && selectedBookingForBookItAgain) {
            const bookedOffers = bookedOffersForMatching
            const hasMultipleOffers = selectedOffers.length > 1

            selectedOffers.forEach((selectedOffer) => {
                if (selectedOffer?.offerId) {
                    const offerTripId = selectedOffer.tripCoverage?.coveredTripId || "";
                    const isShuttleOffer = selectedOffer.products?.[0]?.code === SHUTTLE_PRODUCT_CODE;
                    
                    const offerPassengerRefs = passengerRefs;
                    
                    const offerData: any = {
                        offerId: selectedOffer.offerId,
                        passengerRefs: passengerRefs,
                        placeSelections: isShuttleOffer ? [] : offerPassengerRefs.map((passengerRef: string) => {
                            const passenger = selectedTrip.passengers.find((p) => p.externalRef === passengerRef);
                            
                            const selectedOfferLegIds = selectedOffer.tripCoverage?.coveredLegIds || []
                            
                            return selectedOfferLegIds.map(selectedLegId => {
                                const newBookingTripLeg = train?.legs?.find(leg => leg.id === selectedLegId)
                                
                                if (!newBookingTripLeg) return null
                                
                                const reservation = selectedOffer?.reservationOfferParts?.find((res) => 
                                    res.passengerRefs.includes(passengerRef) && 
                                    res?.tripCoverage?.coveredLegIds?.includes(selectedLegId)
                                );
                                
                                const newBookingTripLegStartCode = newBookingTripLeg.timedLeg.start.stopPlaceRef.stopPlaceRef
                                const newBookingTripLegEndCode = newBookingTripLeg.timedLeg.end.stopPlaceRef.stopPlaceRef
                                
                                const selectedOfferProductCode = selectedOffer.products?.[0]?.code
                                const bookOffer = bookedOffers.find((bo) => {
                                    const hasMatchingProduct = bo.products?.some(p => p.code === selectedOfferProductCode)
                                    const hasPassengerAdmission = bo.admissions.some(adm => adm.passengerIds.includes(passenger?.id || ""))
                                    const bookedTripForOffer = selectedBookingForBookItAgain.trips.find(d => d.id === bo?.tripCoverage?.coveredTripId)
                                    const hasMatchingLeg = bookedTripForOffer?.legs.some(leg => 
                                        leg.timedLeg.start.stopPlaceRef.stopPlaceRef === newBookingTripLegStartCode &&
                                        leg.timedLeg.end.stopPlaceRef.stopPlaceRef === newBookingTripLegEndCode
                                    )
                                    return hasMatchingProduct && hasPassengerAdmission && hasMatchingLeg
                                })

                                if (!bookOffer) return null
                                
                                const bookedTripForOffer = selectedBookingForBookItAgain.trips.find(d => d.id === bookOffer?.tripCoverage?.coveredTripId)
                                const bookedTripLeg = bookedTripForOffer?.legs.find(leg => 
                                    leg.timedLeg.start.stopPlaceRef.stopPlaceRef === newBookingTripLegStartCode &&
                                    leg.timedLeg.end.stopPlaceRef.stopPlaceRef === newBookingTripLegEndCode
                                )
                                
                                let passengerReservation: IReservation | undefined = bookOffer?.reservations?.find((res) => 
                                    res.passengerIds.includes(passenger?.id || "") && 
                                    bookedTripLeg && res?.tripCoverage?.coveredLegIds?.includes(bookedTripLeg.id)
                                );
                                
                                if (!passengerReservation && bookedTripLeg) {
                                    passengerReservation = bookOffer?.reservations?.find((res) => 
                                        res.passengerIds.includes(passenger?.id || "") && 
                                        res?.tripCoverage?.coveredLegIds?.some(legId => {
                                            const leg = bookedTripForOffer?.legs.find(l => l.id === legId)
                                            return leg && 
                                                leg.timedLeg.start.stopPlaceRef.stopPlaceRef === newBookingTripLegStartCode &&
                                                leg.timedLeg.end.stopPlaceRef.stopPlaceRef === newBookingTripLegEndCode
                                        })
                                    )
                                }
                                
                                if (!passengerReservation) {
                                    passengerReservation = bookOffer?.reservations?.find((res) => 
                                        res.passengerIds.includes(passenger?.id || "")
                                    )
                                }
                                
                                let coachNumber = passengerReservation?.placeAllocation.reservedPlaces[0]?.coachNumber || ""
                                let placeNumber = passengerReservation?.placeAllocation.reservedPlaces[0]?.placeNumbers[0] || ""
                                
                                if (isShuttleOffer && !newBookingTripLeg) {
                                    return null;
                                }
                                
                                const legStopCodes = hasMultipleOffers && newBookingTripLegStartCode && newBookingTripLegEndCode
                                    ? `${newBookingTripLegStartCode}-${newBookingTripLegEndCode}`
                                    : undefined
                                
                                const placeData: any = {
                                    coachNumber: coachNumber,
                                    placeNumber: placeNumber,
                                    passengerRef: passengerRef
                                }
                                
                                if (legStopCodes) {
                                    placeData.legStopCodes = legStopCodes
                                }
                                
                                return {
                                    reservationId: reservation?.id || "",
                                    places: [placeData],
                                    tripLegCoverage: {
                                        tripId: offerTripId,
                                        legId: newBookingTripLeg?.id || "",
                                    }
                                }
                            }).filter(item => item !== null)
                        }).flatMap(d => d)
                    }
                    offers.push(offerData)
                }
            })
        }
        actionResult.data = {
            offers,
            price
        }

        // /**
        //  * For post booking,
        //  * We get offerId for all legs
        //  * There is one case, we have duplicated offer id into @var offers, if train leg have same offer id.
        //  * Then we need to removed duplicated offer id from @var offers
        //  * This case happen with Abu Dhabi to Al Dhaid                  
        // */
        // const uniqueOffers = Array.from(
        //     new Map(offers.map((item: any) => [item.offerId, item])).values()
        // );

        // const bookingParams = {
        //     uniqueOffers,
        //     passengerSpecifications: selectedTrip.passengers,
        //     externalRef: uuid(),
        // };

        // // Construct the IBookingRequest object from the prepared bookingParams
        // const newBookingRequest: any = {
        //     offers: bookingParams.uniqueOffers,
        //     passengerSpecifications: bookingParams.passengerSpecifications,
        //     externalRef: bookingParams.externalRef,
        // };

        // // Update the booking request state in Recoil
        // updateRecoilState<IBookingRequest>('bookingRequestAtom', newBookingRequest);

        // actionResult.data = {
        //     newBookingRequest,
        //     price
        // }
        return "success"
    }

    async changeDateAndShowMessage(markSelectedDateAsDisable: ISelectedDisabledCalenderDate, markSelectedDateAsSoldOut: ISelectedDisabledCalenderDate, selectedRange: [Dayjs | null, Dayjs | null], direction: string, isOfferSoldOut: boolean) {
        const cloneMarkSelectedDateAsDisable = { ...markSelectedDateAsDisable }
        const cloneMarkSelectedDateAsSoldOut = { ...markSelectedDateAsSoldOut }
        if (direction === TripTypeEnum.OUT_BOUND) {
            let cloneSelectedRange = [...selectedRange]
            if (isOfferSoldOut) {
                const outboundSoldOutDates = [...(cloneMarkSelectedDateAsSoldOut.OUT_BOUND || [])]
                outboundSoldOutDates.push(selectedRange[0]?.format("YYYY-MM-DD") || "")
                cloneMarkSelectedDateAsSoldOut.OUT_BOUND = outboundSoldOutDates
            } else {
                const outboundDisabledDates = [...(cloneMarkSelectedDateAsDisable.OUT_BOUND || [])]
                outboundDisabledDates.push(selectedRange[0]?.format("YYYY-MM-DD") || "")
                cloneMarkSelectedDateAsDisable.OUT_BOUND = outboundDisabledDates
            }
            cloneSelectedRange[0] = null
            updateRecoilState("selectedDateRangeStateAtom", cloneSelectedRange);

        } else {
            let cloneSelectedRange = [...selectedRange]
            if (direction === TripTypeEnum.IN_BOUND) {
                if (isOfferSoldOut) {
                    const inboundSoldOutDates = [...(cloneMarkSelectedDateAsSoldOut.IN_BOUND || [])]
                    inboundSoldOutDates.push(selectedRange[0]?.format("YYYY-MM-DD") || "")
                    cloneMarkSelectedDateAsSoldOut.IN_BOUND = inboundSoldOutDates
                }
                else {
                    const inboundDisabledDates = [...(cloneMarkSelectedDateAsDisable.IN_BOUND || [])]
                    inboundDisabledDates.push(selectedRange[1]?.format("YYYY-MM-DD") || "")
                    cloneMarkSelectedDateAsDisable.IN_BOUND = inboundDisabledDates
                }
                cloneSelectedRange[1] = null
                updateRecoilState("selectedDateRangeStateAtom", cloneSelectedRange);
            }
        }
        updateRecoilState("markSelectedDateAsDisableAtom", cloneMarkSelectedDateAsDisable)
        updateRecoilState("markSelectedDateAsSoldOutAtom", cloneMarkSelectedDateAsSoldOut)
    }

    override async run(): Promise<IDefaultActionResult> {
        let actionResult: IDefaultActionResult = {
            data: null,
            message: "",
            status: "fail"
        }
        const { selectedTrip, selectedRange, direction = "", tripType = "", selectedDatePrice = [], markSelectedDateAsDisable, isDateOfferCheckDone, markSelectedDateAsSoldOut, selectedBookingForBookItAgain, booking } = this.params!
        try {
            if (selectedTrip && selectedRange && selectedRange[0]) {
                // if (direction === "IN_BOUND") {
                //     if (selectedRange[1] == null) return actionResult
                // }
                if (direction === TripTypeEnum.OUT_BOUND || direction === TripTypeEnum.IN_BOUND) {
                    const isDateOfferCheckDoneData = { ...isDateOfferCheckDone }
                    isDateOfferCheckDoneData[direction] = false
                    updateRecoilState("isDateOfferCheckDoneAtom", isDateOfferCheckDoneData)
                }
                // Store the initial search state                
                const searchStateObject: SearchStateType = {
                    from: selectedTrip.from,
                    fromName: selectedTrip.fromName,
                    to: selectedTrip.to,
                    toName: selectedTrip.toName,
                    date: null,
                    calc: "",
                    outboundDate: dayjs(selectedTrip?.departureDateTime),
                    passengers: this.convertPassengersToCount(selectedTrip.passengers)
                };
                updateRecoilState("searchStateAtom", searchStateObject);
                // updateRecoilState("isCheckoutFromExchangeAtom", false);

                const {
                    totalPassengerCount,
                    passengerCounts,
                } = getSearchSelectedPassenger(searchStateObject.passengers);

                let searchParams: any = {}
                searchParams = {
                    departureTime: `${selectedRange[0].format("YYYY-MM-DDT")}${selectedTrip?.departureTime || "00:00"}:00.000`,
                    arrivalTime: "",
                    origin: selectedTrip.from,
                    destination: selectedTrip.to,
                    totalPassengerCount: totalPassengerCount,
                    passengerCounts: passengerCounts,
                    trainType: TrainTypeEnum.outbound,
                    resetValue: true,
                    fromShuttle: false,
                    toShuttle: false,
                    //I set selectedTripPassenger as in searchParams because I need it into getSearchResultAction
                    selectedTripPassenger: selectedTrip.passengers,
                    // selectedTripPassenger: selectedTrip.passengers.map(p => {
                    //     return {
                    //         age: p.age,
                    //         externalRef: p.externalRef,
                    //         type: "PERSON",
                    //         prmNeeds: p.prmNeeds,
                    //         card: p.cards
                    //     }
                    // }),
                    isOnlyOfferReturn: true,
                    isFromBookItAgain: true,
                }
                if (direction === TripTypeEnum.IN_BOUND) {
                    searchParams = {
                        ...searchParams,
                        arrivalTime: { ...searchParams }.departureTime,
                        origin: searchParams.destination,
                        destination: searchParams.origin,
                        departureTime: selectedRange[1] && `${selectedRange[1].format("YYYY-MM-DDT")}${selectedTrip?.inboundData?.departureTime || "00:00"}:00.000`,
                        // departureTime: selectedTrip.arrivalDateTime,
                        // departureTime: selectedTrip?.inboundData?.departureDateTime || "",
                        trainType: TrainTypeEnum.inbound,
                        isOnlyOfferReturn: true,
                        isFromBookItAgain: true,
                        fromName: selectedTrip.toName,
                        toName: selectedTrip.fromName,
                        resetValue: true
                    }
                }

                getSearchResultAction.setParams(searchParams);
                await getSearchResultAction.execute();
                if (getSearchResultAction.status.executionStatus === ExecutionStatus.SUCCESS) {
                    if (getSearchResultAction.result?.result === "true") {
                        const offerResponse: IOffersResponse = getSearchResultAction?.result?.data || []

                        processBookItAgainOfferAction.setParams({
                            isInbound: direction === TripTypeEnum.IN_BOUND,
                            offers: offerResponse.offers,
                            selectedTrip: selectedTrip,
                            trips: offerResponse.trips
                        })
                        await processBookItAgainOfferAction.execute()
                        if (processBookItAgainOfferAction.status.executionStatus === ExecutionStatus.SUCCESS) {
                            const tripOffers: ITripOfferModel[] = processBookItAgainOfferAction.result!

                            if (tripOffers && tripOffers.length > 0) {
                                convertOffersToTrainsAction.setParams({
                                    offersData: tripOffers,
                                    type: direction === TripTypeEnum.IN_BOUND ? TrainTypeEnum.inbound : TrainTypeEnum.outbound
                                })
                                await convertOffersToTrainsAction.execute()

                                if (convertOffersToTrainsAction.status.executionStatus === ExecutionStatus.SUCCESS) {
                                    const convertedTrainTrips = convertOffersToTrainsAction?.result || []
                                    let trainTrip = convertedTrainTrips[0]
                                    let trainClass = ""
                                    if (booking) {
                                        getAllOfferWithoutExchangeAction.setParams(booking.bookedOffers)
                                        await getAllOfferWithoutExchangeAction.execute()
                                        if (isActionRunSuccessfully(getAllOfferWithoutExchangeAction)) {
                                            const bookedOffers: IBookedOffer[] = getAllOfferWithoutExchangeAction?.result || []
                                            if (bookedOffers.length > 0) {
                                                getAllTripWithoutExchangeAction.setParams({
                                                    bookedOffers: bookedOffers,
                                                    trips: booking.trips
                                                })
                                                await getAllTripWithoutExchangeAction.execute()
                                                const trips: IBookingTypeTrip[] = getAllTripWithoutExchangeAction?.result || []
                                                if (direction === TripTypeEnum.OUT_BOUND) {
                                                    const outboundTrip = trips.find(d => d.direction === TripTypeEnum.OUT_BOUND)
                                                    const outboundOffer = bookedOffers.find(d => d?.tripCoverage?.coveredTripId === outboundTrip?.id)
                                                    if (outboundOffer) {
                                                        trainClass = getTrainClassTypeBaseOnProduct(outboundOffer?.products || []) || ""
                                                    }
                                                }
                                                else {
                                                    if (direction === TripTypeEnum.IN_BOUND) {
                                                        const outboundTrip = trips.find(d => d.direction === TripTypeEnum.IN_BOUND)
                                                        const inboundOffer = bookedOffers.find(d => d?.tripCoverage?.coveredTripId === outboundTrip?.id)
                                                        if (inboundOffer) {
                                                            trainClass = getTrainClassTypeBaseOnProduct(inboundOffer?.products || []) || ""
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }

                                    if (convertedTrainTrips.length > 1) {
                                        const classMatchedTrain = convertedTrainTrips.find((train) => {
                                            switch (trainClass) {
                                                case "standardValue":
                                                    return Boolean(train?.priceInfo?.standardValue?.offerId);
                                                case "standardPro":
                                                    return Boolean(train?.priceInfo?.standardPro?.offerId);
                                                case "premium":
                                                case "premiumValue":
                                                    return Boolean(train?.priceInfo?.premium?.offerId);
                                                case "standardDuo":
                                                    return Boolean(train?.priceInfo?.standardDuo?.offerId);
                                                case "standardQuad":
                                                    return Boolean(train?.priceInfo?.standardQuad?.offerId);
                                                case "premiumDuo":
                                                    return Boolean(train?.priceInfo?.premiumDuo?.offerId);
                                                case "premiumQuad":
                                                    return Boolean(train?.priceInfo?.premiumQuad?.offerId);
                                                case "":
                                                    return Boolean(train?.priceInfo?.shuttle?.offerId);
                                                default:
                                                    return false;
                                            }
                                        });
                                        if (classMatchedTrain) {
                                            trainTrip = classMatchedTrain;
                                        }
                                    }

                                    if (!trainTrip) {
                                        updateRecoilState("dateSelectedFromCalendarAtom", false);
                                        return actionResult;
                                    }

                                    trainTrip.selectedPriceClass = trainClass as ITrainTypeClass | null

                                    updateRecoilState("selectedTrainStateAtom", trainTrip);
                                    let bookingStatus = await this.checkOfferAvailableOrNot(
                                        trainTrip,
                                        selectedTrip,
                                        actionResult,
                                        selectedBookingForBookItAgain,
                                        direction
                                    );
                                    if (bookingStatus === "success") {
                                        const updatedData = [
                                            ...selectedDatePrice,
                                            {
                                                [direction]: {
                                                    price: actionResult?.data?.price || 0,
                                                    date: direction === TripTypeEnum.OUT_BOUND ? selectedRange[0].format("YYYY-MM-DD") : selectedRange[1] ? selectedRange[1].format("YYYY-MM-DD") : ""
                                                }
                                            }]
                                        updateRecoilState("selectedDatePriceAtom", updatedData);

                                        actionResult.message = ""
                                        actionResult.status = "success"
                                        updateRecoilState("isHyperFastStateAtom", true)

                                        /**
                                             * For post booking,
                                             * We get offerId for all legs
                                             * There is one case, we have duplicated offer id into @var offers, if train leg have same offer id.
                                             * Then we need to removed duplicated offer id from @var offers
                                             * This case happen with Abu Dhabi to Al Dhaid                  
                                            */
                                        const uniqueOffers = Array.from(
                                            new Map((actionResult?.data?.offers || []).map((item: any) => [item.offerId, item])).values()
                                        );
                                        if (direction === TripTypeEnum.OUT_BOUND && tripType === "oneWayTrip") {
                                            // Construct the IBookingRequest object from the prepared bookingParams
                                            const newBookingRequest: any = {
                                                offers: uniqueOffers,
                                                passengerSpecifications: selectedTrip.passengers,
                                                externalRef: uuid(),
                                            };
                                            updateRecoilState<IBookingRequest>('bookingRequestAtom', newBookingRequest);
                                            actionResult.data.newBookingRequest = newBookingRequest
                                        } else {
                                            if (tripType === "roundTrip") {
                                                if (direction === TripTypeEnum.OUT_BOUND) {
                                                    const newBookingRequest: any = {
                                                        offers: uniqueOffers,
                                                        passengerSpecifications: selectedTrip.passengers,
                                                        externalRef: uuid(),
                                                    };
                                                    actionResult.data = {
                                                        ...actionResult.data || {},
                                                        newBookingRequest
                                                    }
                                                } else {
                                                    if (direction === TripTypeEnum.IN_BOUND) {
                                                        actionResult.data.uniqueOffers = uniqueOffers
                                                    }
                                                }
                                            }
                                        }
                                    } else {
                                        // showNotification("error", "Please select another date.", "Offer not available on selected date")
                                        if (actionResult.status === "fail") {
                                            this.changeDateAndShowMessage(markSelectedDateAsDisable, markSelectedDateAsSoldOut, selectedRange, direction, false)
                                        }
                                        updateRecoilState("dateSelectedFromCalendarAtom", false)
                                    }
                                }
                            }
                            else {
                                //Else part means offer does not have correct matching time 
                                // with existing booking(book it again booking)
                                if (actionResult.status === "fail") {
                                    this.changeDateAndShowMessage(markSelectedDateAsDisable, markSelectedDateAsSoldOut, selectedRange, direction, false)
                                }
                                updateRecoilState("dateSelectedFromCalendarAtom", false)
                                // showNotification("error", "Please select another date.", "Offer not available on selected date")
                            }
                        }
                    }
                }
            }
            try {
                const element = document.getElementById("details_arrow");
                if (element) {

                    element.style.display = "";
                }
            } catch (error) {

            }
        }
        catch (error) {
            console.log("Error during booking again:", error);
            actionResult.message = "Something went wrong"
        } finally {
            if (actionResult.status === "success") {
                if (direction === TripTypeEnum.OUT_BOUND || direction === TripTypeEnum.IN_BOUND) {
                    const isDateOfferCheckDoneData = { ...isDateOfferCheckDone }
                    isDateOfferCheckDoneData[direction] = true
                    updateRecoilState("isDateOfferCheckDoneAtom", isDateOfferCheckDoneData)
                }
            }
            return actionResult
        }
    }
}

export const bookItAgainDateClickAction = new BookItAgainDateClickAction();
